import React, {useEffect, useState} from 'react';
import {Wrapper} from './EventCreateModal.styles';
import {ModalCloseFunction} from '../../../../components/Modal';
import {useBaseScreenHook} from '../../../../logic/core/base-screen.hook';
import {useParams} from 'react-router-dom';
import {Organization, ProceedingType} from '../../../../logic/organizations/organizations.model';
import FormField from '../../../../components/FormField';
import {useForm} from '../../../../logic/core/form.hook';
import Select from '../../../../components/Select';
import {Body} from '../../../../logic/bodies/bodies.model';
import {getBodies} from '../../../../logic/bodies/bodies';
import Radio from '../../../../components/Radio';
import Input from '../../../../components/Input';
import {EventBody, EventType} from '../../../../logic/events/events.model';
import {createEvent} from '../../../../logic/events/events';
import {Violations} from '../../../../logic/core/failures';
import {AppRoutes} from '../../../../logic/core/routes';
import {useAppStateHook} from "../../../../logic/core/appState.hook";

const EventCreateModal = ({onClose}: { onClose: ModalCloseFunction }) => {
    const {t, setError, renderError, navigate} = useBaseScreenHook();
    const {organizationId} = useParams();
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [bodies, setBodies] = useState<Body[] | null>(null);
    const {getOrganization} = useAppStateHook();
    const [circularStartMode, setCircularStartMode] = useState<'manual' | 'scheduled'>('manual');

    const {renderValidationMessage, formControl, formValid, formValue, setValidations} = useForm<EventBody>({
        bodyId: {
            value: '',
            validators: [(v) => !v ? t('validations.required') : null]
        },
        proceedings: {
            value: '',
            disabled: true,
        },
        name: {
            value: '',
            validators: [(v) => !v ? t('validations.required') : null]
        },
        type: {
            value: '',
            validators: [(v) => !v ? t('validations.required') : null]
        },
        startDate: {
            value: '',
            disabled: true,
            validators: [(v) => {
                if (!v) return null;
                const date = new Date(v);
                if (isNaN(date.getTime())) {
                    return t('errors.wrongDate');
                }
                if (date <= new Date()) {
                    return t('event.error.newStartDateMustBeInTheFuture');
                }
                return null;
            }]
        },
        finishDate: {
            value: '',
            disabled: true,
            validators: [(v) => {
                if (!v) return null;
                const date = new Date(v);
                if (isNaN(date.getTime())) {
                    return t('errors.wrongDate');
                }
                if (date <= new Date()) {
                    return t('event.error.newFinishDateMustBeInTheFuture');
                }
                return null;
            }]
        },
    });

    const selectedBodyId = formControl('bodyId').value;
    const selectedType: EventType = formControl('type').value;

    useEffect(() => {
        if (!bodies) {
            return;
        }
        const body = bodies.find((b) => b.id === selectedBodyId);

        if (body?.legalFormBody.selectableProceedingType) {
            formControl('proceedings').enable();
        } else {
            formControl('proceedings').disable();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBodyId, bodies]);

    useEffect(() => {
        if (!selectedType) {
            return;
        }

        if (selectedType === EventType.LIVE) {
            formControl('startDate').enable();
            formControl('finishDate').disable();
        } else {
            formControl('finishDate').enable();
            if (circularStartMode === 'scheduled') {
                formControl('startDate').enable();
            } else {
                formControl('startDate').disable();
                formControl('startDate').patchValue('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType, circularStartMode]);

    useEffect(() => {
        _getBodies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!organizationId) {
            return;
        }
        getOrganization().then((res) => {
            if (res.isFailure()) {
                return;
            }
            setOrganization(res.data!);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const _getBodies = () => {
        if (!organizationId) {
            return;
        }
        getBodies(organizationId).then((res) => {
            if (res.isFailure()) {
                setError(res.failure!);
                return;
            }
            setBodies(res.data!);
        });
    };

    const _submit = async () => {
        if (!formValid()) {
            return;
        }

        if (selectedType === EventType.LIVE) {
            if (!formValue.startDate) {
                setValidations({ startDate: t('validations.required') });
                return;
            }
        } else if (selectedType === EventType.CIRCULAR) {
            if (!formValue.finishDate) {
                setValidations({ finishDate: t('validations.required') });
                return;
            }
            if (circularStartMode === 'scheduled') {
                if (!formValue.startDate) {
                    setValidations({ startDate: t('validations.required') });
                    return;
                }
                if (new Date(formValue.finishDate) <= new Date(formValue.startDate)) {
                    setValidations({ finishDate: t('event.error.finishDateMustBeAfterStartDate') });
                    return;
                }
            }
        }

        const payload: EventBody = {
            ...formValue,
            startDate: (selectedType === EventType.LIVE || circularStartMode === 'scheduled') && formValue.startDate
                ? new Date(formValue.startDate).toISOString()
                : null,
            finishDate: selectedType === EventType.CIRCULAR && formValue.finishDate
                ? new Date(formValue.finishDate).toISOString()
                : null,
        };

        const res = await createEvent(organizationId!, payload);
        if (res.isViolations()) {
            setValidations((res.failure! as Violations).getValidations());
            return;
        }
        if (res.isFailure()) {
            setError(res.failure!);
            return;
        }

        onClose();
        localStorage.setItem('event-source-location', window.location.pathname + window.location.search);
        if (formValue.type === EventType.LIVE) {
            navigate({
                pathname: `/${AppRoutes.organization}/${organizationId!}/${AppRoutes.event}/${res.data}`,
            });
        } else {
            navigate({
                pathname: `/${AppRoutes.organization}/${organizationId!}/${AppRoutes.event}/${res.data}/${AppRoutes.eventAgenda}`,
                search: `first=true`,
            });
        }
    };

    return <Wrapper>
        <div className="organization-name">
            {organization?.name}
        </div>
        <div className="title">
            {t('eventCreateModal.title')}
        </div>

        <FormField>
            <div className="label">
                {t('eventCreateModal.body') + ' *'}
            </div>
            <div className="hint">
                {t('eventCreateModal.bodyHint')}
            </div>

            {bodies && bodies.length === 0 ? (
                <div style={{ margin: '10px 0', padding: '14px', background: 'var(--lightGrayColor)', borderRadius: '8px', border: '1px solid var(--cardBorderColor)' }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--fontColor)' }}>
                        {t('eventCreateModal.noBodiesInfo')}
                    </p>
                    <button
                        className="button"
                        type="button"
                        onClick={() => {
                            onClose();
                            navigate(`/${AppRoutes.organization}/${organizationId}/${AppRoutes.host}/${AppRoutes.bodies}`);
                        }}
                    >
                        {t('eventCreateModal.addBodyButton')}
                    </button>
                </div>
            ) : (
                <Select
                    currentValue={formControl('bodyId').value}
                    onChange={formControl('bodyId').patchValue}
                >
                    <option/>
                    {bodies?.map((p) => {
                        return <option value={p.id} key={p.id}>{p.name}</option>
                    })}
                </Select>
            )}
            {renderValidationMessage('bodyId')}
        </FormField>

        {
            formControl('proceedings').control.disabled
                ? null
                : <>
                    <FormField>
                        <div className="label">
                            {t('eventCreateModal.proceedings') + ' *'}
                        </div>
                    </FormField>
                    <FormField>
                        <Radio labelElement={<div className="label">
                            {t('eventCreateModal.proceedingOrdinary')}
                        </div>}>
                            <input type="radio" value={ProceedingType.ORDINARY} name="proceedings"
                                   checked={formControl('proceedings').value === ProceedingType.ORDINARY}
                                   onChange={() => {
                                       formControl('proceedings').patchValue(ProceedingType.ORDINARY);
                                   }}/>
                        </Radio>
                    </FormField>
                    <FormField>
                        <Radio labelElement={<div className="label">
                            {t('eventCreateModal.proceedingExtraordinary')}
                        </div>}>
                            <input type="radio" value={ProceedingType.EXTRAORDINARY} name="proceedings"
                                   checked={formControl('proceedings').value === ProceedingType.EXTRAORDINARY}
                                   onChange={() => {
                                       formControl('proceedings').patchValue(ProceedingType.EXTRAORDINARY);
                                   }}/>
                        </Radio>
                        {renderValidationMessage('proceedings')}
                    </FormField>
                </>
        }

        <FormField>
            <Input label={t('eventCreateModal.name') + ' *'}>
                <input type="text"
                       value={formControl('name').value}
                       onChange={(e) => formControl('name').patchValue(e.target.value)}/>
            </Input>
            {renderValidationMessage('name')}
        </FormField>

        <FormField>
            <div className="label">
                {t('eventCreateModal.type') + ' *'}
            </div>
        </FormField>
        <FormField>
            <Radio labelElement={
                <div className="label">
                    <strong>{t('eventCreateModal.typeLive')}</strong>
                    <div className="label-description">
                        {t('eventCreateModal.liveDescription')}
                    </div>
                </div>}>
                <input type="radio" value={EventType.LIVE} name="type"
                       checked={formControl('type').value === EventType.LIVE}
                       onChange={() => {
                           formControl('type').patchValue(EventType.LIVE);
                       }}/>
            </Radio>
        </FormField>
        <FormField>
            <Radio labelElement={<div className="label">
                <strong>{t('eventCreateModal.typeCircular')}</strong>
                <div className="label-description">
                    {t('eventCreateModal.circularDescription')}
                </div>
            </div>}>
                <input type="radio" value={EventType.CIRCULAR} name="type"
                       checked={formControl('type').value === EventType.CIRCULAR}
                       onChange={() => {
                           formControl('type').patchValue(EventType.CIRCULAR);
                       }}/>
            </Radio>
            {renderValidationMessage('type')}
        </FormField>

        {
            selectedType === EventType.CIRCULAR ? (
                <>
                    <FormField>
                        <div className="label" style={{ marginTop: '10px' }}>
                            {t('eventCreateModal.circularStartQuestion')}
                        </div>
                    </FormField>
                    <FormField>
                        <Radio labelElement={<div className="label">
                            {t('eventCreateModal.circularStartManual')}
                            <div className="label-description">
                                {t('eventCreateModal.circularStartManualHint')}
                            </div>
                        </div>}>
                            <input
                                type="radio"
                                value="manual"
                                name="circularStartMode"
                                checked={circularStartMode === 'manual'}
                                onChange={() => setCircularStartMode('manual')}
                            />
                        </Radio>
                    </FormField>
                    <FormField>
                        <Radio labelElement={<div className="label">
                            {t('eventCreateModal.circularStartScheduled')}
                        </div>}>
                            <input
                                type="radio"
                                value="scheduled"
                                name="circularStartMode"
                                checked={circularStartMode === 'scheduled'}
                                onChange={() => setCircularStartMode('scheduled')}
                            />
                        </Radio>
                    </FormField>

                    {circularStartMode === 'scheduled' ? (
                        <FormField>
                            <Input label={t('eventCreateModal.startDate') + ' *'}>
                                <input
                                    type="datetime-local"
                                    value={formControl('startDate').value}
                                    onChange={(e) => formControl('startDate').patchValue(e.target.value)}
                                />
                            </Input>
                            {renderValidationMessage('startDate')}
                        </FormField>
                    ) : null}

                    <FormField>
                        <Input label={t('eventCreateModal.finishDate') + ' *'}>
                            <input
                                type="datetime-local"
                                value={formControl('finishDate').value}
                                onChange={(e) => formControl('finishDate').patchValue(e.target.value)}
                            />
                        </Input>
                        {renderValidationMessage('finishDate')}
                    </FormField>
                </>
            ) : null
        }

        {
            selectedType === EventType.LIVE ? (
                <FormField>
                    <Input label={t('eventCreateModal.startDate') + ' *'}>
                        <input
                            type="datetime-local"
                            value={formControl('startDate').value}
                            onChange={(e) => formControl('startDate').patchValue(e.target.value)}
                        />
                    </Input>
                    {renderValidationMessage('startDate')}
                </FormField>
            ) : null
        }

        <div className="buttons">
            <button className="button text muted" onClick={onClose}>
                {t('eventCreateModal.cancel')}
            </button>

            <button className={'button'} onClick={_submit} disabled={bodies !== null && bodies.length === 0}>
                {t('eventCreateModal.save')}
            </button>
        </div>

        <div className="next-step">
            {t('eventCreateModal.nextStep')}
        </div>

        {renderError()}
    </Wrapper>;
};

export default EventCreateModal;
